import { MAX_UNDO_STACK_SIZE } from '@/constants/constants';
import { AppState, AuthSource, AuthState, defaultState, UPLOAD_PROGRESS_STATE } from '@/context/appContext';
import { shouldStackAction } from '@/context/functions';
import { EmbeddedItemFontResolution, FALLBACK_FONT_ID } from '@/fonts/fontMatching';
import {
  pdfCoordinatesToCanvasCoordinates,
  pdfPointsToCanvasPoints,
  scaleTextBoxItemToCanvas,
} from '@/helpers/pdfHelpers';
import {
  EditableTextItem,
  FormFields,
  PageAnnotations,
  PageImageItems,
  PageItems,
  PageTextItems,
  TextCoordinates,
} from '@/types';
import { PageShapes, Shape } from '@/types/shapes';
import { addObjectToAppState, renderOrderToZindexMap } from '@/functions/renderOrderFunctions';
import { PdfEditingContext, SubscriptionPlanDto, SubscriptionType, UserDto } from '@editar-pdf/api';
import AnnotationsReducer, { AnnotationActionType, AnnotationEvent } from './annotationsReducer';
import formsReducer, { FormsActionTypes, FormsReducerAction } from './formsReducer';
import pageImagesReducer, { ImageItemsReducerAction, ImageItemsReducerActionType } from './imagesReducer';
import renderOrderReducer, { RenderOrderReducerAction, RenderOrderReducerActionType } from './renderOrderReducer';
import shapesReducer, { ShapesReducerAction, ShapesReducerActionType } from './shapesReducer';
import { TextRenderingMode } from '@/types/pdfJsTypes';
import { generateId } from '@/helpers/ids';
import { DirtyTextBoxes } from '@/components/PdfEditor/types';

export enum AppStateActions {
  SET_DOCUMENT = 'SET_DOCUMENT',
  SET_DOCUMENT_PASSWORD = 'SET_DOCUMENT_PASSWORD',
  SET_PROCESSED_DOCUMENT = 'SET_PROCESSED_DOCUMENT',
  SET_DOCUMENT_LOAD_STATE = 'SET_DOCUMENT_LOAD_STATE',
  SET_DOCUMENT_READY_STATE = 'SET_DOCUMENT_READY_STATE',
  SET_UPLOADED_FILE_ID = 'SET_UPLOADED_FILE_ID',
  SET_SAVED_CHANGES = 'SET_SAVED_CHANGES',

  UPDATE_SHAPE_PAGES = 'UPDATE_SHAPE_PAGES',
  UPDATE_FORMS_PAGES = 'UPDATE_FORMS_PAGES',

  SELECT_IMAGE = 'SELECT_IMAGE',
  SELECT_ANNOTATION = 'SELECT_ANNOTATION',

  ADD_PAGE = 'ADD_PAGE',
  DELETE_PAGE = 'DELETE_PAGE',
  DUPLICATE_PAGE = 'DUPLICATE_PAGE',
  SET_PAGES = 'SET_PAGES',

  ADD_TEXT_ITEM = 'ADD_TEXT_ITEM',
  SET_TEXT_ITEMS = 'SET_TEXT_ITEMS',
  UPDATE_TEXT_ITEM = 'UPDATE_TEXT_ITEM',
  REMAP_TEXT_ITEM_FONTS = 'REMAP_TEXT_ITEM_FONTS',
  SET_SCALE = 'SET_SCALE',

  RESET_STATE = 'RESET_STATE',

  UNDO = 'UNDO',
  REDO = 'REDO',

  SET_AUTH_STATE = 'SET_AUTH_STATE',
  SET_AUTH_TOKEN = 'SET_AUTH_TOKEN',
  RESTORE_AUTH_TOKEN = 'RESTORE_AUTH_TOKEN',
  SET_NEED_PAYMENT = 'SET_NEED_PAYMENT',
  SET_USER_INFO = 'SET_USER_INFO',
  SET_SUBSCRIPTION_PLANS = 'SET_SUBSCRIPTION_PLANS',
  SET_LAST_PAYMENT_DATE = 'SET_LAST_PAYMENT_DATE',
  SET_UPLOAD_PROGRESS_STATE = 'SET_UPLOAD_PROGRESS_STATE',
  SET_DIRTY_TEXT_BOXES = 'SET_DIRTY_TEXT_BOXES',
}

type AppContextAction =
  | {
      type: AppStateActions.RESET_STATE | AppStateActions.UNDO | AppStateActions.REDO;
      payload?: undefined;
    }
  | {
      type: AppStateActions.SET_DOCUMENT;
      payload: { document: File | null };
    }
  | {
      type: AppStateActions.SET_DOCUMENT_PASSWORD;
      payload: { password: string | null };
    }
  | {
      type: AppStateActions.SET_PROCESSED_DOCUMENT;
      payload: { document: File | null };
    }
  | {
      type: AppStateActions.SET_DOCUMENT_LOAD_STATE;
      payload: { isDocumentLoaded: boolean };
    }
  | {
      type: AppStateActions.SET_DOCUMENT_READY_STATE;
      payload: { isDocumentReady: boolean };
    }
  | {
      type: AppStateActions.SET_UPLOADED_FILE_ID;
      payload: { id: string | null };
    }
  | {
      type: AppStateActions.SET_SAVED_CHANGES;
      payload?: PdfEditingContext;
    }
  | {
      type: AppStateActions.UPDATE_SHAPE_PAGES;
      payload: { shapes: PageShapes };
    }
  | {
      type: AppStateActions.SELECT_IMAGE;
      payload: { id: string | null };
    }
  | {
      type: AppStateActions.SELECT_ANNOTATION;
      payload: { id: string | null };
    }
  | {
      type: AppStateActions.UPDATE_FORMS_PAGES;
      payload: { formFields: FormFields };
    }
  | {
      type: AppStateActions.ADD_PAGE;
      payload: { pageIndex: number };
    }
  | {
      type: AppStateActions.DELETE_PAGE;
      payload: { pageIndex: number };
    }
  | {
      type: AppStateActions.DUPLICATE_PAGE;
      payload: { pageIndex: number };
    }
  | {
      type: AppStateActions.SET_PAGES;
      payload: { pageItems: PageItems };
    }
  | {
      type: AppStateActions.ADD_TEXT_ITEM;
      payload: { pageNumber: number; textItem: EditableTextItem };
    }
  | {
      type: AppStateActions.UPDATE_TEXT_ITEM;
      payload: { pageNumber: number; textItem: Partial<EditableTextItem>; preserveRenderingMode?: boolean };
    }
  | {
      type: AppStateActions.SET_TEXT_ITEMS;
      payload: { textItems: PageTextItems };
    }
  | {
      type: AppStateActions.REMAP_TEXT_ITEM_FONTS;
      /** internal pdf.js name → how the item resolves (keep embedded face, or move to a catalog font) */
      payload: { fontNameMap: Record<string, EmbeddedItemFontResolution> };
    }
  | {
      type: AppStateActions.SET_SCALE;
      payload: { scale: number };
    }
  | {
      type: AppStateActions.SET_AUTH_STATE;
      payload: { state?: AuthState; force?: boolean };
    }
  | {
      type: AppStateActions.SET_AUTH_TOKEN;
      payload?: { token: string; source: AuthSource };
    }
  | {
      type: AppStateActions.SET_NEED_PAYMENT;
      payload: { needPayment: boolean; preselectedTariff?: SubscriptionType };
    }
  | {
      type: AppStateActions.SET_USER_INFO;
      payload: { userInfo: UserDto };
    }
  | {
      type: AppStateActions.SET_SUBSCRIPTION_PLANS;
      payload: { subscriptionPlans?: SubscriptionPlanDto[] };
    }
  | {
      type: AppStateActions.SET_LAST_PAYMENT_DATE;
      payload: { lastPaymentDate: string };
    }
  | {
      type: AppStateActions.SET_UPLOAD_PROGRESS_STATE;
      payload: UPLOAD_PROGRESS_STATE | null;
    }
  | {
      type: AppStateActions.SET_DIRTY_TEXT_BOXES;
      payload: { dirtyTextBoxes: DirtyTextBoxes };
    };

/**
 * Carries a page's text items into a re-keyed map (page insert/delete/duplicate).
 */
const carryPageTextItems = (target: PageTextItems, toPage: number, source: EditableTextItem[] | undefined) => {
  if (source !== undefined) target[toPage] = source;
};

function appContextReducer(state: AppState, action: AppContextAction): AppState {
  switch (action.type) {
    case AppStateActions.SET_DOCUMENT: {
      const { document } = action.payload;
      const newState = document === null ? appContextReducer(state, { type: AppStateActions.RESET_STATE }) : state;

      return {
        ...newState,
        document: action.payload.document,
        // Reset document ready state when loading a new document
        // (will be set to true when document is rendered for the first time)
        isDocumentReady: false,
        // Clear any previously saved context whenever a new document is loaded so it isn't
        // mistakenly applied to a document that has no associated saved state (e.g. fresh uploads).
        // EditorWrapper re-sets this immediately via SET_SAVED_CHANGES when loading a saved document.
        savedChanges: undefined,
      };
    }
    case AppStateActions.SET_DOCUMENT_PASSWORD: {
      return {
        ...state,
        documentPassword: action.payload.password,
      };
    }
    case AppStateActions.SET_PROCESSED_DOCUMENT: {
      return {
        ...state,
        processedDocument: action.payload.document,
      };
    }
    case AppStateActions.SET_DOCUMENT_LOAD_STATE: {
      return {
        ...state,
        isDocumentLoaded: action.payload.isDocumentLoaded,
      };
    }
    case AppStateActions.SET_DOCUMENT_READY_STATE: {
      return {
        ...state,
        isDocumentReady: action.payload.isDocumentReady,
      };
    }
    case AppStateActions.SET_UPLOADED_FILE_ID: {
      return {
        ...state,
        uploadedFileId: action.payload.id,
      };
    }
    case AppStateActions.SET_SAVED_CHANGES: {
      return {
        ...state,
        savedChanges: action.payload,
      };
    }
    case AppStateActions.UPDATE_SHAPE_PAGES: {
      return {
        ...state,
        shapes: { ...state.shapes, ...action.payload.shapes },
      };
    }
    case AppStateActions.SELECT_IMAGE: {
      return {
        ...state,
        selectedImageId: action.payload.id,
      };
    }
    case AppStateActions.SELECT_ANNOTATION: {
      return {
        ...state,
        selectedAnnotationId: action.payload.id,
      };
    }
    case AppStateActions.UPDATE_FORMS_PAGES: {
      return {
        ...state,
        formFields: { ...state.formFields, ...action.payload.formFields },
      };
    }
    case AppStateActions.ADD_PAGE: {
      const pageIndex = action.payload.pageIndex;
      const pageIndices = Object.keys(state.pagesMetadata).map((p) => parseInt(p));

      const newPageMetadata: PageItems = {};
      const newShapes: PageShapes = {};
      const newImages: PageImageItems = {};
      const newAnnotations: PageAnnotations = {};
      const newFormFields: FormFields = {};
      const newTextItems: PageTextItems = {};
      const newAddedPages = [...state.addedPages.map((p) => (p >= pageIndex ? p + 1 : p)), pageIndex].sort(
        (a, b) => a - b,
      );

      pageIndices.forEach((p) => {
        if (p >= pageIndex) {
          newPageMetadata[p + 1] = { ...state.pagesMetadata[p], pageIndex: state.pagesMetadata[p].pageIndex + 1 };
          newShapes[p + 1] = state.shapes[p];
          newImages[p + 1] = state.images[p];
          newAnnotations[p + 1] = state.annotations[p];
          newFormFields[p + 1] = state.formFields[p];
          carryPageTextItems(newTextItems, p + 1, state.textItems[p]);
        } else if (p < pageIndex) {
          newPageMetadata[p] = state.pagesMetadata[p];
          newShapes[p] = state.shapes[p];
          newImages[p] = state.images[p];
          newAnnotations[p] = state.annotations[p];
          newFormFields[p] = state.formFields[p];
          carryPageTextItems(newTextItems, p, state.textItems[p]);
        }
      });

      // Use previous page's dimensions as template, or fallback to page 0
      const templatePageIndex = pageIndex > 0 ? pageIndex - 1 : 0;
      newPageMetadata[pageIndex] = {
        ...state.pagesMetadata[templatePageIndex],
        originalPageNumber: -1,
        id: generateId(),
      };
      newShapes[pageIndex] = [];
      newImages[pageIndex] = [];
      newAnnotations[pageIndex] = [];
      newFormFields[pageIndex] = [];
      newTextItems[pageIndex] = [];

      return {
        ...state,
        shapes: newShapes,
        images: newImages,
        annotations: newAnnotations,
        formFields: newFormFields,
        textItems: newTextItems,
        pagesMetadata: newPageMetadata,
        addedPages: newAddedPages,
      };
    }
    case AppStateActions.DELETE_PAGE: {
      // TODO: delete the pages objects from the render order array
      if (Object.keys(state.pagesMetadata).length === 1) {
        return state;
      }

      const pageIndex: number = action.payload.pageIndex;
      const originalPageNumber = state.pagesMetadata[pageIndex].originalPageNumber;

      const newPageMetadata: PageItems = {};
      const newShapes: PageShapes = {};
      const newImages: PageImageItems = {};
      const newAnnotations: PageAnnotations = {};
      const newFormFields: FormFields = {};
      const newTextItems: PageTextItems = {};

      Object.keys(state.pagesMetadata).forEach((pageNumber) => {
        const p = parseInt(pageNumber);

        if (p > pageIndex) {
          newPageMetadata[p - 1] = { ...state.pagesMetadata[p], pageIndex: state.pagesMetadata[p].pageIndex - 1 };
          newShapes[p - 1] = state.shapes[p] ?? [];
          newImages[p - 1] = state.images[p] ?? [];
          newAnnotations[p - 1] = state.annotations[p] ?? [];
          newFormFields[p - 1] = state.formFields[p] ?? [];
          carryPageTextItems(newTextItems, p - 1, state.textItems[p]);
        } else if (p < pageIndex) {
          newPageMetadata[p] = state.pagesMetadata[p];
          newShapes[p] = state.shapes[p] ?? [];
          newImages[p] = state.images[p] ?? [];
          newAnnotations[p] = state.annotations[p] ?? [];
          newFormFields[p] = state.formFields[p] ?? [];
          carryPageTextItems(newTextItems, p, state.textItems[p]);
        }
      });

      let newDeletedPages: number[] = [...state.deletedPages];
      let newAddedPages: number[] = state.addedPages;

      if (originalPageNumber === -1) {
        newAddedPages = state.addedPages.filter((p) => p !== pageIndex);
      } else {
        newDeletedPages = [...newDeletedPages, originalPageNumber];
      }

      newAddedPages = newAddedPages.map((p) => (p >= pageIndex ? p - 1 : p));

      // Bug: page comes back from the dead

      return {
        ...state,
        shapes: newShapes,
        images: newImages,
        annotations: newAnnotations,
        formFields: newFormFields,
        textItems: newTextItems,
        pagesMetadata: newPageMetadata,
        addedPages: newAddedPages,
        deletedPages: newDeletedPages,
      };
    }
    case AppStateActions.DUPLICATE_PAGE: {
      const pageIndex = action.payload.pageIndex;
      const pageIndices = Object.keys(state.pagesMetadata).map((p) => parseInt(p));
      const sourcePage = state.pagesMetadata[pageIndex];
      if (!sourcePage) return state;

      const newPageMetadata: PageItems = {};
      const newShapes: PageShapes = {};
      const newImages: PageImageItems = {};
      const newAnnotations: PageAnnotations = {};
      const newFormFields: FormFields = {};
      const newTextItems: PageTextItems = {};
      const newAddedPages = [...state.addedPages.map((p) => (p >= pageIndex + 1 ? p + 1 : p)), pageIndex + 1].sort(
        (a, b) => a - b,
      );

      pageIndices.forEach((p) => {
        if (p > pageIndex) {
          newPageMetadata[p + 1] = { ...state.pagesMetadata[p], pageIndex: state.pagesMetadata[p].pageIndex + 1 };
          newShapes[p + 1] = state.shapes[p];
          newImages[p + 1] = state.images[p];
          newAnnotations[p + 1] = state.annotations[p];
          newFormFields[p + 1] = state.formFields[p];
          carryPageTextItems(newTextItems, p + 1, state.textItems[p]);
        } else {
          newPageMetadata[p] = state.pagesMetadata[p];
          newShapes[p] = state.shapes[p];
          newImages[p] = state.images[p];
          newAnnotations[p] = state.annotations[p];
          newFormFields[p] = state.formFields[p];
          carryPageTextItems(newTextItems, p, state.textItems[p]);
        }
      });

      const sourceShapes = state.shapes[pageIndex] ?? [];
      newShapes[pageIndex + 1] = sourceShapes.map((s) => ({ ...s, id: generateId() }));

      const sourceImages = state.images[pageIndex] ?? [];
      newImages[pageIndex + 1] = sourceImages.map((img) => ({ ...img, id: generateId() }));

      const sourceAnnotations = state.annotations[pageIndex] ?? [];
      newAnnotations[pageIndex + 1] = sourceAnnotations.map((a) => ({ ...a, id: generateId() }));

      const sourceFormFields = state.formFields[pageIndex] ?? [];
      newFormFields[pageIndex + 1] = sourceFormFields.map((f) => {
        const id = generateId();
        return { ...f, id, name: `${f.type}_${id}` };
      });

      const sourceTextItems = state.textItems[pageIndex] ?? [];
      newTextItems[pageIndex + 1] = sourceTextItems.map((t) => ({ ...t, id: generateId(), isDuplicated: true }));

      const sourceThumbnailPageNumber =
        sourcePage.originalPageNumber !== -1 ? sourcePage.originalPageNumber : sourcePage.thumbnailPageNumber;

      newPageMetadata[pageIndex + 1] = {
        ...sourcePage,
        originalPageNumber: -1,
        thumbnailPageNumber: sourceThumbnailPageNumber,
        id: generateId(),
        pageIndex: pageIndex + 1,
      };

      const newRenderOrder = [
        ...state.pdfObjectsRenderOrder,
        ...newShapes[pageIndex + 1].map((s) => s.id),
        ...newImages[pageIndex + 1].map((img) => img.id),
        ...newAnnotations[pageIndex + 1].map((a) => a.id),
        ...newFormFields[pageIndex + 1].map((f) => f.id),
        ...newTextItems[pageIndex + 1].map((t) => t.id),
      ];

      return {
        ...state,
        shapes: newShapes,
        images: newImages,
        annotations: newAnnotations,
        formFields: newFormFields,
        textItems: newTextItems,
        pagesMetadata: newPageMetadata,
        addedPages: newAddedPages,
        pdfObjectsRenderOrder: newRenderOrder,
        pdfOBjectsZIndexMap: renderOrderToZindexMap(newRenderOrder),
      };
    }
    case AppStateActions.SET_PAGES: {
      return {
        ...state,
        pagesMetadata: action.payload.pageItems,
      };
    }
    case AppStateActions.ADD_TEXT_ITEM: {
      const pageNumber = action.payload.pageNumber;
      const pageItems = state.textItems[pageNumber];

      // Sparse invariant: no entry means useClientTextLoader hasn't finished parsing
      // this page yet. Drop the add rather than seeding an array: a fabricated entry
      // makes the loader's skip guard treat the page as already parsed, so its real
      // text never loads (SET_SCALE below guards the same way for the same reason).
      // Callers gate on this too; this is the backstop that keeps a stray dispatch
      // from throwing and taking the editor down with it.
      if (pageItems === undefined) return state;

      // Registered in the render order right away, the way ADD_SHAPE does it, so a box the user
      // just placed sits on top of what was already there. Parsed document text stays
      // unregistered (z 0) and therefore below every shape — which is what lets an erase
      // rectangle hide the original text while leaving a box added on top of it alone.
      return addObjectToAppState(
        {
          ...state,
          textItems: { ...state.textItems, [pageNumber]: [...pageItems, action.payload.textItem] },
        },
        action.payload.textItem.id,
      );
    }
    case AppStateActions.UPDATE_TEXT_ITEM: {
      const { pageNumber, textItem, preserveRenderingMode } = action.payload;
      const pageItems = state.textItems[pageNumber];

      // Same sparse backstop as ADD_TEXT_ITEM above: nothing to update on a page the
      // loader hasn't parsed, and a stray dispatch must not throw out of the reducer.
      if (pageItems === undefined) return state;

      const newTextItems = pageItems.map((t: EditableTextItem) => {
        if (t.id === textItem.id) {
          return {
            ...t,
            ...(preserveRenderingMode === true ? {} : { renderingMode: TextRenderingMode.FILL }),
            ...textItem,
          };
        }

        return t;
      });

      return {
        ...state,
        textItems: { ...state.textItems, [pageNumber]: newTextItems },
      };
    }
    case AppStateActions.SET_TEXT_ITEMS: {
      // Incoming items carry valid pdf coordinates but their canvas coordinates
      // are computed at whatever scale the parser happened to know about (the
      // client text loader emits at scale=1). Rescale to current state.scale on
      // insert so the rest of the editor — which reads currentCanvasCoordinates
      // directly for layout — stays consistent with whatever scale is committed
      // right now. SET_SCALE already does the same rescale-from-pdf-coords pass
      // over existing items; this is the symmetric pass for newly-arriving ones.
      const incoming = action.payload.textItems;
      const rescaled: PageTextItems = {};
      Object.keys(incoming).forEach((pageNumber) => {
        const pageNumberInt = parseInt(pageNumber);
        const pageMeta = state.pagesMetadata[pageNumberInt];
        if (!pageMeta) {
          rescaled[pageNumberInt] = incoming[pageNumberInt];
          return;
        }
        rescaled[pageNumberInt] = incoming[pageNumberInt].map((item) =>
          scaleTextBoxItemToCanvas(item, pageMeta.page, state.scale),
        );
      });
      return {
        ...state,
        textItems: { ...state.textItems, ...rescaled },
      };
    }
    case AppStateActions.REMAP_TEXT_ITEM_FONTS: {
      // Server font matches arrived after some pages were already parsed.
      // Items on a usable embedded face keep it (the matched font becomes a
      // fallback tier, tracked in the documentFonts registry, not here); items
      // on unusable faces move to the resolved catalog font. Applied to the
      // undo/redo snapshots too, so stepping through history can't resurrect a
      // pre-resolution state. Not an undoable action itself (it's a data
      // refinement, not a user edit).
      const { fontNameMap } = action.payload;

      const remapPageItems = (textItems: PageTextItems): PageTextItems => {
        const remapped: PageTextItems = {};
        Object.keys(textItems).forEach((pageNumber) => {
          const pageNumberInt = parseInt(pageNumber);
          const pageItems = textItems[pageNumberInt];
          // Editor state restored from IndexedDB can still carry keys holding undefined,
          // written by releases before carryPageTextItems existed (structured clone keeps
          // undefined values). Skip them rather than throw.
          if (pageItems === undefined) return;

          remapped[pageNumberInt] = pageItems.map((item) => {
            if (!item.isEmbedded) return item;
            const resolution = fontNameMap[item.originalFontName];
            // Absent key = font the loader never saw: degrade to the fallback
            // family rather than keep an unrenderable internal name.
            if (resolution === undefined) {
              return { ...item, fontName: FALLBACK_FONT_ID, isEmbedded: false };
            }
            if (resolution.useEmbeddedFace) return item;
            return { ...item, fontName: resolution.fontId, isEmbedded: false };
          });
        });
        return remapped;
      };

      return {
        ...state,
        textItems: remapPageItems(state.textItems),
        pastStates: state.pastStates.map((past) => ({ ...past, textItems: remapPageItems(past.textItems) })),
        futureStates: state.futureStates.map((future) => ({ ...future, textItems: remapPageItems(future.textItems) })),
      };
    }
    case AppStateActions.SET_SCALE: {
      const { scale } = action.payload;

      const newTextItems: PageTextItems = {};
      const newFormFields: FormFields = {};
      const newImages: PageImageItems = {};
      const newShapes: PageShapes = {};
      const newAnnotations: PageAnnotations = {};

      // Loop over page metadata, scale each page
      Object.keys(state.pagesMetadata).forEach((pageNumber) => {
        const pageNumberInt = parseInt(pageNumber);
        // textItems is loaded async per page via useClientTextLoader, so a page
        // may not be present here yet. Only rescale pages that actually have items
        // and keep textItems sparse: fabricating an empty array for an unloaded
        // page makes the loader treat it as already-parsed (its skip guard keys
        // off presence in textItems) and never load its text — long documents
        // would then render pages beyond the first viewport with no editable text.
        const item = state.textItems[pageNumberInt];
        if (item !== undefined) {
          newTextItems[pageNumberInt] = item.map((textBoxItem) =>
            scaleTextBoxItemToCanvas(textBoxItem, state.pagesMetadata[pageNumberInt].page, scale),
          );
        }

        const forms = state.formFields[pageNumberInt] ?? [];
        newFormFields[pageNumberInt] = forms.map((textBoxItem) => {
          return {
            ...textBoxItem,
            canvasCoordinates: pdfCoordinatesToCanvasCoordinates(
              textBoxItem.pdfCoordinates,
              state.pagesMetadata[pageNumberInt].page,
              scale,
            ) as TextCoordinates,
          };
        });

        newImages[pageNumberInt] = (state.images[pageNumberInt] ?? []).map((image) => {
          return {
            ...image,
            currentCanvasCoordinates: pdfCoordinatesToCanvasCoordinates(
              {
                ...image.currentPdfCoordinates,
                y: image.currentPdfCoordinates.y + image.currentPdfCoordinates.height,
              },
              state.pagesMetadata[pageNumberInt].page,
              scale,
            ),
          };
        });

        newShapes[pageNumberInt] = (state.shapes[pageNumberInt] ?? []).map((shape: Shape) => {
          return {
            ...shape,
            canvasPoints: pdfPointsToCanvasPoints(shape.pdfPoints, state.pagesMetadata[pageNumberInt].page, scale),
          };
        });

        newAnnotations[pageNumberInt] = (state.annotations[pageNumberInt] ?? []).map((annotation) => {
          return {
            ...annotation,
            currentCanvasCoordinates: pdfCoordinatesToCanvasCoordinates(
              {
                ...annotation.currentPdfCoordinates,
                y: annotation.currentPdfCoordinates.y + annotation.currentPdfCoordinates.height,
              },
              state.pagesMetadata[pageNumberInt].page,
              scale,
            ),
          };
        });
      });

      return {
        ...state,
        textItems: newTextItems,
        formFields: newFormFields,
        images: newImages,
        shapes: newShapes,
        annotations: newAnnotations,
        scale,
      };
    }
    case AppStateActions.RESET_STATE: {
      return {
        ...state,
        ...defaultState,
      };
    }
    case AppStateActions.SET_AUTH_STATE: {
      return {
        ...state,
        authState: action.payload,
      };
    }
    case AppStateActions.SET_AUTH_TOKEN: {
      // Pure state transition only. The HTTP/persistence side effects (axios Authorization
      // header + local storage) are applied by applyAuthToken at the App dispatch layer.
      return {
        ...state,
        authToken: action.payload,
      };
    }
    case AppStateActions.SET_NEED_PAYMENT:
    case AppStateActions.SET_USER_INFO:
    case AppStateActions.SET_SUBSCRIPTION_PLANS:
    case AppStateActions.SET_LAST_PAYMENT_DATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case AppStateActions.SET_UPLOAD_PROGRESS_STATE: {
      return {
        ...state,
        uploadProgressState: action.payload,
      };
    }
    case AppStateActions.SET_DIRTY_TEXT_BOXES: {
      return {
        ...state,
        dirtyTextBoxes: action.payload.dirtyTextBoxes,
      };
    }
    default:
      return state;
  }
}

export type AppStateAction =
  | AppContextAction
  | ImageItemsReducerAction
  | AnnotationEvent
  | FormsReducerAction
  | ShapesReducerAction
  | RenderOrderReducerAction;

function isAppContextAction(action: AppStateAction): action is AppContextAction {
  return (Object.values(AppStateActions) as unknown[]).includes(action.type);
}

function isImagesAction(action: AppStateAction): action is ImageItemsReducerAction {
  return (Object.values(ImageItemsReducerActionType) as unknown[]).includes(action.type);
}

function isAnnotationAction(action: AppStateAction): action is AnnotationEvent {
  return (Object.values(AnnotationActionType) as unknown[]).includes(action.type);
}

function isFormsAction(action: AppStateAction): action is FormsReducerAction {
  return (Object.values(FormsActionTypes) as unknown[]).includes(action.type);
}

function isShapeAction(action: AppStateAction): action is ShapesReducerAction {
  return (Object.values(ShapesReducerActionType) as unknown[]).includes(action.type);
}

function isRenderAction(action: AppStateAction): action is RenderOrderReducerAction {
  return (Object.values(RenderOrderReducerActionType) as unknown[]).includes(action.type);
}

export const appReducer = (state: AppState, action: AppStateAction): AppState => {
  if (isAppContextAction(action)) {
    return appContextReducer(state, action);
  }

  if (isImagesAction(action)) {
    return pageImagesReducer(state, action);
  }

  if (isAnnotationAction(action)) {
    return AnnotationsReducer(state, action);
  }

  if (isFormsAction(action)) {
    return formsReducer(state, action);
  }

  if (isShapeAction(action)) {
    return shapesReducer(state, action);
  }

  if (isRenderAction(action)) {
    return renderOrderReducer(state, action);
  }

  return state;
};

const UNDO_REDO_ACTIONS = [
  ShapesReducerActionType.ADD_SHAPE,
  ShapesReducerActionType.UPDATE_SHAPE,
  ShapesReducerActionType.DELETE_SHAPE,
  ShapesReducerActionType.DUPLICATE_SHAPE,

  AppStateActions.ADD_PAGE,
  AppStateActions.DELETE_PAGE,
  AppStateActions.DUPLICATE_PAGE,

  AppStateActions.ADD_TEXT_ITEM,
  AppStateActions.UPDATE_TEXT_ITEM,

  //FormsActionTypes.SET_FORM_FIELD_TYPE_TO_INSERT,
  FormsActionTypes.ADD_FORM_FIELD,
  FormsActionTypes.DUPLICATE_FORM_FIELD,
  FormsActionTypes.UPDATE_FORM_FIELD,
  //FormsActionTypes.SELECT_FORM_FIELD,
  FormsActionTypes.DELETE_FORM_FIELD,

  ImageItemsReducerActionType.ADD,
  ImageItemsReducerActionType.DUPLICATE,
  ImageItemsReducerActionType.UPDATE,
  ImageItemsReducerActionType.DELETE,
  ImageItemsReducerActionType.ROTATE,

  AnnotationActionType.ADD,
  AnnotationActionType.UPDATE,
  AnnotationActionType.DELETE,
  AnnotationActionType.WIDTH,
];

export const appReducerWrapper = (state: AppState, action: AppStateAction): AppState => {
  switch (action.type) {
    case AppStateActions.UNDO: {
      if (state.pastStates.length === 0) return state;
      const pastState = state.pastStates[state.pastStates.length - 1];
      return {
        ...pastState,
        pastStates: state.pastStates.slice(0, state.pastStates.length - 1),
        futureStates: [state, ...state.futureStates],
      };
    }
    case AppStateActions.REDO: {
      if (state.futureStates.length === 0) return state;
      const futureState = state.futureStates[0];
      return {
        ...futureState,
        pastStates: [...state.pastStates, state],
        futureStates: state.futureStates.slice(1),
      };
    }
    default: {
      const newState = appReducer(state, action);

      if (!UNDO_REDO_ACTIONS.includes(action.type as any)) return newState;

      const currentState = { ...state };

      currentState.pastStates = [];
      currentState.futureStates = [];

      let newPastStates: AppState[] = [];

      if (!shouldStackAction(action, state.lastActionType)) {
        const pastStates = state.pastStates.slice(0, state.pastStates.length - 1);
        newPastStates = [...pastStates, currentState];
      } else {
        newPastStates = [...state.pastStates, currentState];
      }

      let newUrlsToRevokePerPastState = state.urlsToRevokePerPastState;

      if (newPastStates.length > MAX_UNDO_STACK_SIZE) {
        state.urlsToRevokePerPastState[0]?.forEach((url: string) => {
          URL.revokeObjectURL(url);
        });

        newPastStates = newPastStates.slice(1);
        newUrlsToRevokePerPastState = state.urlsToRevokePerPastState.slice(1);
      }

      return {
        ...newState,
        pastStates: newPastStates,
        futureStates: [],
        lastActionType: action.type,
        urlsToRevokePerPastState: newUrlsToRevokePerPastState,
      };
    }
  }
};
